/* eslint-disable react/prop-types -- style/onEachFeature return Leaflet options, not React props */
import React from 'react';
import { GeoJSON } from 'react-leaflet';
import type { Feature, FeatureCollection } from 'geojson';
import type L from 'leaflet';

import './index.scss';

export interface PolygonLayerFeatureProperties {
  name?: string;
  color?: string;
  value?: number;
  [key: string]: unknown;
}

export interface PolygonLayerProps {
  data: FeatureCollection | null | undefined;
  onPolygonClick?: (feature: Feature, properties: PolygonLayerFeatureProperties) => void;
  selectedFeatureName?: string | null;
}

const defaultStyle = {
  fillOpacity: 0.4,
  weight: 1,
  color: '#fff', // tema: fixo (desenhado sobre o mapa, igual nos dois temas)
};

const selectedStyle = {
  fillOpacity: 0.9,
  weight: 3,
  color: '#eee', // tema: fixo (desenhado sobre o mapa, igual nos dois temas)
};

export default function PolygonLayer({
  data,
  onPolygonClick,
  selectedFeatureName,
}: PolygonLayerProps) {
  if (!data || !data.features?.length) return null;

  return (
    <GeoJSON
      data={data}
      style={(feature) => {
        const props = feature?.properties as PolygonLayerFeatureProperties | undefined;
        const name = props?.name ?? (props as any)?.nome;
        const isSelected = selectedFeatureName != null
          && String(name) === String(selectedFeatureName);
        const base = {
          fillColor: props?.color ?? '#3388ff', // tema: fixo (desenhado sobre o mapa, igual nos dois temas)
          ...(isSelected ? selectedStyle : defaultStyle),
          color: isSelected ? selectedStyle.color : defaultStyle.color,
        };
        return base;
      }}
      onEachFeature={(feature, layer: L.Layer) => {
        const props = (feature?.properties ?? {}) as PolygonLayerFeatureProperties;
        const name = props?.name ?? (props as PolygonLayerFeatureProperties &
        { nome?: string })?.nome;
        const tooltipText = name != null && String(name).trim() !== '' ? String(name).trim() : null;

        const tooltipOptions: L.TooltipOptions = {
          permanent: false,
          direction: 'top',
          sticky: true,
          className: 'polygon-layer-tooltip',
          offset: [0, -8],
        };

        type LayerWithTooltip = L.Layer & {
          bindTooltip?: (content: string, opts?: L.TooltipOptions) => L.Layer;
          openTooltip?: (latlng?: L.LatLngExpression) => L.Layer;
          closeTooltip?: () => L.Layer;
          eachLayer?: (fn: (l: L.Layer) => void) => void;
        };

        const fireClick = () => {
          if (onPolygonClick) onPolygonClick(feature, props);
        };

        const setupTooltipOnLayer = (target: LayerWithTooltip) => {
          if (!tooltipText || typeof target.bindTooltip !== 'function') return;
          target.bindTooltip(tooltipText, tooltipOptions);
          target.on('mouseover', () => { target.openTooltip?.(); });
          target.on('mouseout', () => { target.closeTooltip?.(); });
        };

        const setupInteractionOnLayer = (target: LayerWithTooltip) => {
          setupTooltipOnLayer(target);
          target.on('click', fireClick);
        };

        const layerWithEach = layer as LayerWithTooltip;
        if (typeof layerWithEach.eachLayer === 'function') {
          layerWithEach.eachLayer((sublayer) => setupInteractionOnLayer(sublayer as LayerWithTooltip)); // eslint-disable-line max-len
        } else {
          setupInteractionOnLayer(layerWithEach);
        }
      }}
    />
  );
}
